// src/Services/DashboardService.js
import { ServiceProvider } from "./ServiceProvider";
import apiCall from "../utils/apiInterceptor";

class DashboardService {
    constructor() {
        this.baseURL = ServiceProvider.dashboard.base;
    }

    /**
     * Get dashboard statistics based on range
     * @param {string} range - 'day', 'week', 'month'
     * @returns {Promise} API response
     */
    async getDashboardStats(range = 'weekly') {
        try {
            const response = await apiCall(`${this.baseURL}/dashboard?range=${range}`, {
                method: 'GET',
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data.data;
        } catch (error) {
            console.error('Error fetching dashboard stats:', error);
            throw error;
        }
    }

    /**
     * Transform API response to match component structure
     * @param {Object} apiData - Raw API response
     * @param {string} range - Selected time range
     * @returns {Object} Transformed data
     */
    transformDashboardData(apiData, range) {
        const totalCount = apiData.total_count || 0;

        // Extract counts from the nested status structure
        const byStatusType = apiData.by_status_type || {};

        // Calculate overall status counts across all status types
        let completeCount = 0;
        let inProgressCount = 0;
        let readyToCloseCount = 0;
        let pendingCount = 0;

        // Sum up counts from all status types
        Object.values(byStatusType).forEach(statusType => {
            completeCount += statusType.complete?.count || 0;
            inProgressCount += statusType['in-progress']?.count || 0;
            readyToCloseCount += statusType['ready-to-close']?.count || 0;
            pendingCount += statusType.pending?.count || 0;
        });

        // Calculate percentages based on total count
        const completedPercentage = totalCount > 0 ? Math.round((completeCount / totalCount) * 100) : 0;
        const inProgressPercentage = totalCount > 0 ? Math.round((inProgressCount / totalCount) * 100) : 0;
        const readyToClosePercentage = totalCount > 0 ? Math.round((readyToCloseCount / totalCount) * 100) : 0;
        const pendingPercentage = totalCount > 0 ? Math.round((pendingCount / totalCount) * 100) : 0;

        return {
            totalCount,
            progressStatus: {
                completed: completedPercentage,
                inProgress: inProgressPercentage,
                pending: pendingPercentage,
                current: completedPercentage
            },
            readyToClose: {
                readyToClose: readyToClosePercentage,
                incomplete: 100 - readyToClosePercentage
            },
            weeklyData: this.generateWeeklyData(apiData, range),
            rawData: apiData,
            detailedStatus: byStatusType // Include detailed status for debugging
        };
    }

    /**
     * Generate chart data based on range and API data
     * @param {Object} apiData - Raw API response
     * @param {string} range - Selected time range
     * @returns {Array} Chart data points
     */
    generateWeeklyData(apiData, range) {
        const baseCount = apiData.total_count || 0;

        switch (range) {
            case 'day':
                return Array.from({ length: 7 }, (_, i) => ({
                    week: `Day ${i + 1}`,
                    value: Math.max(0, baseCount + Math.floor(Math.random() * 20) - 10)
                }));

            case 'week':
                return Array.from({ length: 7 }, (_, i) => ({
                    week: `W${i + 1}`,
                    value: Math.max(0, baseCount + Math.floor(Math.random() * 30) - 15)
                }));

            case 'month':
                return Array.from({ length: 12 }, (_, i) => ({
                    week: `M${i + 1}`,
                    value: Math.max(0, baseCount * 2 + Math.floor(Math.random() * 50) - 25)
                }));

            default:
                return Array.from({ length: 7 }, (_, i) => ({
                    week: `W${i + 1}`,
                    value: Math.max(0, baseCount + Math.floor(Math.random() * 30) - 15)
                }));
        }
    }

    /**
     * Get detailed BOM list with pagination
     * @param {number} page - Page number
     * @param {number} limit - Items per page
     * @param {string} status - Filter by status
     * @returns {Promise} API response
     */
    async getBomList(page = 1, limit = 10, status = null) {
        try {
            let url = `${this.baseURL}/list?page=${page}&limit=${limit}`;
            if (status) {
                url += `&status=${status}`;
            }

            const response = await apiCall(url, {
                method: 'GET',
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching BOM list:', error);
            throw error;
        }
    }

    /**
     * Get BOM details by ID
     * @param {string} bomId - BOM ID
     * @returns {Promise} API response
     */
    async getBomDetails(bomId) {
        try {
            const response = await apiCall(`${this.baseURL}/details/${bomId}`, {
                method: 'GET',
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching BOM details:', error);
            throw error;
        }
    }

    /**
     * Update BOM status
     * @param {string} bomId - BOM ID
     * @param {string} status - New status
     * @returns {Promise} API response
     */
    async updateBomStatus(bomId, status) {
        try {
            const response = await apiCall(`${this.baseURL}/update/${bomId}`, {
                method: 'PATCH',
                body: JSON.stringify({ status })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error updating BOM status:', error);
            throw error;
        }
    }
}

// Create singleton instance
const dashboardService = new DashboardService();

export default dashboardService;